import { Injectable, inject } from '@angular/core';
import { 
  Firestore, 
  collectionGroup, 
  query, 
  orderBy, 
  getDocs,
  collection,
  doc,
  getDoc,
  where
} from '@angular/fire/firestore';
import { Observable, from, map, catchError, of, switchMap, forkJoin } from 'rxjs';
import { CollectionTables } from '../models/CollectionTables.model';
import { Invoice } from '../models/invoice.model';
import { AuthService } from './auth.service';

export interface InvoiceDocument {
  id: string;
  number: string;
  date: number;
  total: string;
  subtotal: string;
  commission: string;
  currency: string;
  paid: boolean;
  invoice: string; // PDF URL
  requestId: string;
  requestNumber?: string;
  vendorName?: string;
  clientSecret?: string;
  paymentIntent?: string;
  customerId?: string;
  ephemeralKeySecret?: string;
  paymentStarted?: number;
  paymentCompleted?: number;
  country?: string;
}

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  private firestore = inject(Firestore);
  private authService = inject(AuthService);

  constructor() {}

  /**
   * Get all invoices for an administrator across all requests
   * Matches request.service.ts logic: always query by administratorId field
   * 
   * @param administratorId - The administrator ID from employee.administratorId field
   */
  getAllInvoices(administratorId: string): Observable<InvoiceDocument[]> {
    console.log(`🔍 Fetching all invoices for administrator: ${administratorId}`);
    
    // Query root requests collection by administratorId (same as request.service.ts)
    return from(getDocs(query(
      collection(this.firestore, CollectionTables.requests),
      where('administratorId', '==', administratorId)
    ))).pipe(
      switchMap((requestsSnapshot) => {
        if (requestsSnapshot.empty) {
          console.log(`ℹ️ No requests found with administratorId = ${administratorId}`);
          return of([]);
        }

        return this.fetchInvoicesFromRequests(requestsSnapshot);
      }),
      catchError(error => {
        console.error('❌ Error fetching invoices:', error);
        return of([]);
      })
    );
  }

  /**
   * Helper method to fetch invoices from request documents
   */
  private fetchInvoicesFromRequests(requestsSnapshot: any): Observable<InvoiceDocument[]> {
    console.log(`📦 Found ${requestsSnapshot.size} requests, fetching invoices...`);

    // For each request, fetch its invoices from root requests collection
    const invoiceObservables = requestsSnapshot.docs.map((requestDoc: any) => {
      const requestData = requestDoc.data();
      const requestId = requestDoc.id;
      // Use root requests collection path like Flutter
      const invoicesPath = `${CollectionTables.requests}/${requestId}/${CollectionTables.invoices}`;
      
      return from(getDocs(query(
        collection(this.firestore, invoicesPath),
        orderBy('date', 'desc')
      ))).pipe(
        map(invoicesSnapshot => {
          return invoicesSnapshot.docs.map(invoiceDoc => {
            const invoiceData = invoiceDoc.data();
            return {
              id: invoiceDoc.id,
              requestId: requestId,
              requestNumber: requestData['number'] || requestId,
              vendorName: requestData['providerBusinessName'],
              number: invoiceData['number'] || 'N/A',
              date: invoiceData['date'],
              total: invoiceData['total'],
              subtotal: invoiceData['subtotal'],
              commission: invoiceData['commission'],
              currency: invoiceData['currency'] || 'CAD',
              paid: invoiceData['paid'] === true,
              invoice: invoiceData['invoice'],
              clientSecret: invoiceData['clientSecret'],
              paymentIntent: invoiceData['paymentIntent'],
              paymentStarted: invoiceData['paymentStarted'],
              paymentCompleted: invoiceData['paymentCompleted'],
              country: invoiceData['country'],
            } as InvoiceDocument;
          });
        }),
        catchError(error => {
          console.warn(`⚠️ Error fetching invoices for request ${requestId}:`, error);
          return of([]);
        })
      );
    });

    // Combine all invoice arrays
    return (forkJoin(invoiceObservables.length > 0 ? invoiceObservables : [of([])]) as Observable<InvoiceDocument[][]>).pipe(
      map((invoiceArrays) => {
        const allInvoices = invoiceArrays.flat();
        console.log(`✅ Total invoices found: ${allInvoices.length}`);
        return allInvoices.sort((a, b) => (b.date || 0) - (a.date || 0));
      })
    );
  }

  /**
   * Get all unpaid invoices for an administrator (returns old Invoice model for dashboard compatibility)
   */
  /**
   * Get all unpaid invoices for the current administrator
   * Used in dashboard to show recent unpaid invoices
   */
  getUnpaidInvoices(): Observable<Invoice[]> {
    return from(
      (async () => {
        try {
          const currentUser = this.authService.getCurrentUser();
          if (!currentUser) {
            console.error('❌ No current user');
            return [];
          }

          const administratorId = await this.authService.getAdministratorId(currentUser.uid);
          if (!administratorId) {
            console.error('❌ No administrator ID');
            return [];
          }

          console.log('📄 Getting unpaid invoices for administrator:', administratorId);

          // Query requests for this administrator
          const requestsRef = collection(this.firestore, CollectionTables.requests);
          const requestsQuery = query(
            requestsRef,
            where('administratorId', '==', administratorId)
          );
          const requestsSnapshot = await getDocs(requestsQuery);

          if (requestsSnapshot.empty) {
            console.log('ℹ️ No requests found for administrator');
            return [];
          }

          console.log('📋 Found', requestsSnapshot.size, 'requests');

          // Get invoices for each request
          const invoicePromises = requestsSnapshot.docs.map(async (requestDoc) => {
            const invoicesPath = `${CollectionTables.requests}/${requestDoc.id}/${CollectionTables.invoices}`;
            const invoicesSnapshot = await getDocs(
              query(collection(this.firestore, invoicesPath), orderBy('date', 'desc'))
            );

            return invoicesSnapshot.docs
              .map((doc) => {
                const data = doc.data();
                const invoiceDate = data['date'] || Date.now();
                const totalAmount = parseFloat(data['total'] || '0');
                
                return {
                  id: parseInt(doc.id) || 0,
                  invoiceDocId: doc.id, // Keep original Firebase doc ID
                  requestId: requestDoc.id,
                  requestNumber: requestDoc.data()['requestNumber'] || data['number'] || 'N/A',
                  service: requestDoc.data()['service']?.name || 'Service',
                  serviceKey: requestDoc.data()['service']?.key || '',
                  amount: totalAmount,
                  currency: data['currency'] || 'CAD',
                  status: data['paid'] === true ? 'paid' as const : 'unpaid' as const,
                  dueDate: new Date(invoiceDate),
                  createdDate: new Date(invoiceDate),
                  description: `Invoice #${data['number'] || doc.id}`,
                  vendorId: 0,
                  residentId: 0,
                } as Invoice;
              })
              .filter((invoice) => invoice.status === 'unpaid'); // Only unpaid invoices
          });

          const invoiceArrays = await Promise.all(invoicePromises);
          const allInvoices = invoiceArrays.flat();

          // Sort by date descending and take first 5
          const sortedInvoices = allInvoices
            .sort((a, b) => b.createdDate.getTime() - a.createdDate.getTime())
            .slice(0, 5);

          console.log('✅ Found', sortedInvoices.length, 'unpaid invoices for dashboard');
          return sortedInvoices;
        } catch (error) {
          console.error('❌ Error getting unpaid invoices:', error);
          return [];
        }
      })()
    );
  }

  /**
   * Get all invoices (alias for compatibility with calendar)
   */
  getAll(): Observable<Invoice[]> {
    // Returns empty array for calendar compatibility
    return of([]);
  }

  /**
   * Mark an invoice as paid
   */
  markAsPaid(invoiceId: number): Observable<void> {
    // TODO: Implement Firestore update
    return of(void 0);
  }

  /**
   * Get invoices for a specific request (matching Flutter path)
   */
  getRequestInvoices(administratorId: string, requestId: string): Observable<InvoiceDocument[]> {
    // Use root requests collection path like Flutter
    const invoicesPath = `${CollectionTables.requests}/${requestId}/${CollectionTables.invoices}`;
    
    return from(getDocs(query(
      collection(this.firestore, invoicesPath),
      orderBy('date', 'desc')
    ))).pipe(
      map(snapshot => {
        return snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            requestId: requestId,
            number: data['number'] || 'N/A',
            date: data['date'],
            total: data['total'],
            subtotal: data['subtotal'],
            commission: data['commission'],
            currency: data['currency'] || 'CAD',
            paid: data['paid'] === true,
            invoice: data['invoice'],
            clientSecret: data['clientSecret'],
            paymentIntent: data['paymentIntent'],
            paymentStarted: data['paymentStarted'],
            paymentCompleted: data['paymentCompleted'],
            country: data['country'],
          } as InvoiceDocument;
        });
      }),
      catchError(error => {
        console.error('Error fetching request invoices:', error);
        return of([]);
      })
    );
  }

  /**
   * Get a single invoice by ID (matching Flutter path)
   */
  getInvoiceById(administratorId: string, requestId: string, invoiceId: string): Observable<InvoiceDocument | null> {
    // Use root requests collection path like Flutter
    const invoiceDocRef = doc(
      this.firestore,
      `${CollectionTables.requests}/${requestId}/${CollectionTables.invoices}/${invoiceId}`
    );

    return from(getDoc(invoiceDocRef)).pipe(
      map(docSnap => {
        if (!docSnap.exists()) {
          return null;
        }
        const data = docSnap.data();
        return {
          id: docSnap.id,
          requestId: requestId,
          number: data['number'] || 'N/A',
          date: data['date'],
          total: data['total'],
          subtotal: data['subtotal'],
          commission: data['commission'],
          currency: data['currency'] || 'CAD',
          paid: data['paid'] === true,
          invoice: data['invoice'],
          clientSecret: data['clientSecret'],
          paymentIntent: data['paymentIntent'],
          paymentStarted: data['paymentStarted'],
          paymentCompleted: data['paymentCompleted'],
          country: data['country'],
        } as InvoiceDocument;
      }),
      catchError(error => {
        console.error('Error fetching invoice:', error);
        return of(null);
      })
    );
  }

  /**
   * Download invoice PDF
   */
  downloadInvoice(invoiceUrl: string, invoiceNumber: string): void {
    const link = document.createElement('a');
    link.href = invoiceUrl;
    link.download = `Invoice_${invoiceNumber}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Share invoice by copying URL to clipboard
   */
  async shareInvoice(invoiceUrl: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(invoiceUrl);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Fallback method for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = invoiceUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
        return false;
      }
    }
  }
}
